'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import { Button, Icons, LoadingIndicator } from '@commenda/kakashi';
import { FormsTemplateType } from '@commenda/types';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useContext, useMemo, useState } from 'react';
import { useComponentRegistry } from '../../hooks/ComponentRegistryContext';
import { FormContext } from '../../hooks/FormContext';
import type { SubFormFieldBinding } from '@commenda/types';
import { LinkedFormRenderer } from './LinkedFormRenderer';

export const SubFormDialog = ({
  props,
}: {
  props?: Record<string, unknown>;
}) => {
  const {
    keyId,
    trigger,
    dialog,
    fieldBindings,
    preventSameValueForAllBindings,
    sameValueErrorMessage: globalSameValueErrorMessage,
  } = props || {};
  const [open, setOpen] = useState(false);
  const { formData } = useContext(FormContext);
  const componentRegistry = useComponentRegistry();

  // First resolve the key to get liveFormId
  const {
    key,
    isLoading: isLoadingKey,
    error: keyError,
  } = useFormTemplateKey(
    open ? (keyId as string) : null // Only fetch when dialog is open
  );

  // Then fetch the template using the liveFormId from the key
  const {
    template,
    isLoading: isLoadingTemplate,
    error: templateError,
  } = useFormsTemplate(open && key?.liveFormId ? key.liveFormId : null);

  const isLoading = isLoadingKey || isLoadingTemplate;
  const error = keyError || templateError;

  const triggerConfig = trigger as Record<string, unknown> | undefined;
  const dialogConfig = dialog as Record<string, unknown> | undefined;

  const triggerLabel = triggerConfig?.label ?? 'Open Form';
  const triggerVariant =
    (triggerConfig?.variant as 'primary' | 'secondary') ?? 'secondary';
  const triggerSize = (triggerConfig?.size as 'sm' | 'md' | 'lg') ?? 'md';

  // Get title and description from dialog config or fall back to template/key info
  const title =
    dialogConfig?.title ?? template?.name ?? key?.name ?? 'Linked Form';
  const description =
    dialogConfig?.description ?? template?.description ?? key?.description;

  // Build default values for the subform based on fieldBindings
  const subformDefaultValues = useMemo(() => {
    if (!fieldBindings || !Array.isArray(fieldBindings)) return {};

    const defaults: Record<string, unknown> = {};
    (fieldBindings as SubFormFieldBinding[]).forEach((binding) => {
      if (binding.parentFieldName && binding.subformFieldName) {
        const parentValue = formData[binding.parentFieldName];
        if (parentValue !== undefined) {
          defaults[binding.subformFieldName] = parentValue;
        }
      }
    });
    return defaults;
  }, [fieldBindings, formData]);

  // Build parent values map for same-value validation
  const parentValuesForValidation = useMemo(() => {
    if (!fieldBindings || !Array.isArray(fieldBindings)) return {};

    const parentValues: Record<string, unknown> = {};
    (fieldBindings as SubFormFieldBinding[]).forEach((binding) => {
      if (binding.parentFieldName && binding.subformFieldName) {
        parentValues[binding.subformFieldName] =
          formData[binding.parentFieldName];
      }
    });
    return parentValues;
  }, [fieldBindings, formData]);

  const handleSave = () => {
    setOpen(false);
  };

  if (!keyId) {
    return null;
  }

  return (
    <>
      <Button
        variant={triggerVariant}
        size={triggerSize}
        onClick={() => setOpen(true)}
      >
        {String(triggerLabel)}
      </Button>

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-30"
          onClose={() => setOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-bg-overlay opacity-70 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-x-0 bottom-0 flex justify-center">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500"
                  enterFrom="translate-y-full"
                  enterTo="translate-y-0"
                  leave="transform transition ease-in-out duration-500"
                  leaveFrom="translate-y-0"
                  leaveTo="translate-y-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-full">
                    <div className="flex h-[calc(100vh-80px)] flex-col overflow-hidden bg-bg-secondary rounded-t-2xl shadow-xl">
                      <div className="flex items-center justify-center pt-4 pb-0">
                        <div className="h-1.5 w-[95px] rounded-md bg-fg-quinary" />
                      </div>
                      {isLoading && (
                        <div className="flex items-center justify-center py-12 h-full">
                          <LoadingIndicator />
                        </div>
                      )}

                      {error && (
                        <div className="text-center py-12 h-full flex flex-col items-center justify-center">
                          <Icons.AlertCircle className="w-12 h-12 text-text-error mx-auto mb-4" />
                          <p className="txt-medium-medium text-text-primary mb-2">
                            Failed to load form
                          </p>
                          <p className="txt-small-regular text-text-tertiary">
                            {key && !key.liveFormId
                              ? 'This template key does not have an active live form.'
                              : 'The linked form template could not be loaded.'}
                          </p>
                        </div>
                      )}

                      {template && !isLoading && !error && (
                        <LinkedFormRenderer
                          dsl={template.dsl}
                          type={template.type as FormsTemplateType}
                          defaultValues={subformDefaultValues}
                          parentValuesForValidation={parentValuesForValidation}
                          fieldBindings={
                            (fieldBindings as SubFormFieldBinding[]) || []
                          }
                          preventSameValueForAllBindings={Boolean(
                            preventSameValueForAllBindings
                          )}
                          globalSameValueErrorMessage={
                            globalSameValueErrorMessage as string | undefined
                          }
                          title={String(title)}
                          subtitle={
                            description ? String(description) : undefined
                          }
                          onClose={() => setOpen(false)}
                          onSubmit={handleSave}
                          layoutSettings={{
                            hideTopHeader: true,
                          }}
                          embedMode
                          componentRegistry={componentRegistry}
                        />
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
